/**
 * Serviço centralizado de produção de notificações.
 *
 * Todas as inserções na tabela `notifications` passam por este módulo.
 * Fire-and-forget — falhas de notificação não bloqueiam fluxos de negócio.
 */
import { supabase } from '../lib/supabaseClient';

// ============================================================================
// TIPOS
// ============================================================================

export type NotificationType =
  | 'task_assigned'
  | 'task_escalated'
  | 'task_transferred'
  | 'task_returned'
  | 'task_sla_warning'
  | 'task_sla_violated'
  | 'action_assigned'
  | 'problem_assigned'
  | 'stage_entered'
  | 'project_assigned'
  | 'project_role_changed';

interface NotifyInput {
  userId: string;
  organizationId?: string;
  type: NotificationType;
  title: string;
  message?: string;
  link?: string;
  sourceType?: string;
  sourceId?: string;
  severity?: 'info' | 'warning' | 'critical';
  metadata?: Record<string, unknown>;
}

// ============================================================================
// CORE — insert notification (fire-and-forget)
// ============================================================================

async function insertNotification(input: NotifyInput): Promise<void> {
  try {
    // Primary: notifications_v2 (organization-scoped)
    if (input.organizationId) {
      await supabase.from('notifications_v2').insert({
        organization_id: input.organizationId,
        user_id: input.userId,
        type: input.type,
        severity: input.severity ?? 'info',
        title: input.title,
        message: input.message ?? null,
        link: input.link ?? null,
        source_type: input.sourceType ?? null,
        source_id: input.sourceId ?? null,
        read: false,
        created_at: new Date().toISOString(),
      });
    } else {
      // Fallback: legacy notifications table (no org context)
      await supabase.from('notifications').insert({
        user_id: input.userId,
        type: input.type,
        title: input.title,
        message: input.message ?? null,
        link: input.link ?? null,
        metadata: input.metadata ?? null,
        read: false,
        created_at: new Date().toISOString(),
      });
    }
  } catch (err) {
    console.warn('[notificationProducer] Falha ao inserir notificação:', err);
  }
}

// ============================================================================
// PRODUTORES ESPECÍFICOS
// ============================================================================

/** Nova tarefa atribuída ao usuário (claim, assign, etapa entrou no papel) */
export function notifyTaskAssigned(params: {
  userId: string;
  taskTitle: string;
  stageName?: string;
  instanceTitle?: string;
  taskId?: string;
}): void {
  const title = params.stageName
    ? `Nova tarefa: ${params.stageName}`
    : `Nova tarefa atribuída`;
  const message = params.instanceTitle
    ? `Processo: ${params.instanceTitle}`
    : params.taskTitle;
  insertNotification({
    userId: params.userId,
    type: 'task_assigned',
    title,
    message,
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** Tarefa transferida para o usuário */
export function notifyTaskTransferred(params: {
  userId: string;
  stageName: string;
  fromUserName?: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'task_transferred',
    title: `Tarefa transferida: ${params.stageName}`,
    message: params.fromUserName ? `De: ${params.fromUserName}` : undefined,
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** Tarefa devolvida à fila (notifica membros do papel) */
export function notifyTaskReturned(params: {
  userId: string;
  stageName: string;
  byUserName?: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'task_returned',
    title: `Tarefa devolvida à fila: ${params.stageName}`,
    message: params.byUserName ? `Por: ${params.byUserName}` : undefined,
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** Escalonamento */
export function notifyEscalation(params: {
  userId: string;
  stageName: string;
  justification: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'task_escalated',
    title: `Escalonamento: ${params.stageName}`,
    message: params.justification.slice(0, 120),
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** SLA em risco */
export function notifySlaWarning(params: {
  userId: string;
  stageName: string;
  remainingLabel: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'task_sla_warning',
    title: `SLA em risco: ${params.stageName}`,
    message: `Tempo restante: ${params.remainingLabel}`,
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** SLA violado */
export function notifySlaViolated(params: {
  userId: string;
  stageName: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'task_sla_violated',
    title: `SLA violado: ${params.stageName}`,
    message: 'Prazo excedido. Ação imediata necessária.',
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** Ação do plano de ação atribuída */
export function notifyActionAssigned(params: {
  userId: string;
  actionTitle: string;
  deadline?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'action_assigned',
    title: `Ação atribuída: ${params.actionTitle}`,
    message: params.deadline ? `Prazo: ${params.deadline}` : undefined,
    link: '/acoes',
  });
}

/** Problema atribuído */
export function notifyProblemAssigned(params: {
  userId: string;
  problemTitle: string;
  severity?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'problem_assigned',
    title: `Problema atribuído: ${params.problemTitle}`,
    message: params.severity ? `Severidade: ${params.severity}` : undefined,
    link: '/minha-mesa',
  });
}

/** Etapa entrou no papel do usuário (role queue) */
export function notifyStageEntered(params: {
  userId: string;
  stageName: string;
  instanceTitle?: string;
  taskId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    type: 'stage_entered',
    title: `Nova etapa disponível: ${params.stageName}`,
    message: params.instanceTitle ? `Processo: ${params.instanceTitle}` : undefined,
    link: '/minha-mesa',
    metadata: { taskId: params.taskId },
  });
}

/** Usuário adicionado a um projeto */
export function notifyProjectAssigned(params: {
  userId: string;
  organizationId: string;
  projectName: string;
  role: string;
  projectId?: string;
}): void {
  insertNotification({
    userId: params.userId,
    organizationId: params.organizationId,
    type: 'project_assigned',
    title: `Você foi adicionado ao projeto "${params.projectName}"`,
    message: `Papel: ${params.role === 'owner' ? 'Proprietário' : params.role === 'editor' ? 'Editor' : 'Visualizador'}`,
    link: `/projetos/${params.projectId ?? ''}`,
    sourceType: 'project',
    sourceId: params.projectId,
  });
}
